import React, { useState } from 'react'
import qs from 'qs';
import { useLocation, Link } from 'react-router-dom'
import { MEDIA_URL } from '../../axios';

const AllProjectDetails = () => {
  const projectbg = MEDIA_URL + 'Projects/projectbg.png';
  // Fallback images — used only if the project data doesn't provide its own.
  // NOTE: this was the ONLY image source before, so every project showed the
  // same two pictures regardless of which one was clicked. Add real per-project
  // images to your project data (as `images`, or `image1`/`image2`) to fix this properly.
  const fallbackImage1 = MEDIA_URL + 'Projects/Apco/Apco1.png';
  const fallbackImage2 = MEDIA_URL + 'Projects/Apco/Apco2.png';

  const location = useLocation();
  const { projectdetails } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const projectDetails = projectdetails ? JSON.parse(projectdetails) : null;

  const [activeSlide, setActiveSlide] = useState(0);

  if (!projectDetails || !projectDetails[0]) {
    return (
      <div className='flex flex-col items-center justify-center gap-4 py-32 text-center px-6'>
        <p className='darkblue text-2xl font-semibold'>Project not found</p>
        <p className='text-stone-500 max-w-md'>
          This project couldn't be loaded directly. Please go back to the projects list and select a project.
        </p>
        <Link to='/Projects' className='px-6 py-2 rounded-xl text-white bg-gradient-to-r from-[#61acca] to-[#8364c3] font-medium'>
          Back to Projects
        </Link>
      </div>
    );
  }

  const project = projectDetails[0];

  const gallery = project.images && project.images.length > 0
    ? project.images
    : [project.image1 || fallbackImage1, project.image2 || fallbackImage2].filter(Boolean).map((src) => ({ src, alt: project.Association, caption: null }));

  const specRows = [
    ['Association', project.Association],
    ['JV', project.JV],
    ['Lead', project.Lead],
    ['Project Length (Km)', project.Projectlength],
    ['Cost (Cr.)', project.Cost],
  ].filter(([, value]) => value !== undefined && value !== null && value !== '');

  const nextSlide = () => setActiveSlide((i) => (i + 1) % gallery.length);
  const prevSlide = () => setActiveSlide((i) => (i - 1 + gallery.length) % gallery.length);

  return (
    <div className='flex flex-col'>

      <div
        className='relative h-[42vh] md:h-[46vh] w-full flex flex-col justify-end px-6 md:px-16 pb-10'
        style={{ backgroundImage: `url(${projectbg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className='absolute inset-0' style={{ background: 'linear-gradient(to top, rgba(13,22,32,0.9), rgba(13,22,32,0.35))' }} />
        <div className='relative z-10'>
          <p className='text-white text-sm uppercase tracking-widest mb-2'>Projects</p>
          <h1 className='text-white text-3xl md:text-5xl font-bold'>{project.Association}</h1>
        </div>
      </div>

      <div className='max-w-6xl mx-auto w-full px-6 md:px-10 py-14 flex flex-col lg:flex-row gap-12'>

        <div className='lg:w-[55%] flex flex-col gap-10'>

          {specRows.length > 0 && (
            <div className='bg-white rounded-2xl border border-stone-100 shadow-md overflow-hidden'>
              {specRows.map(([label, value], i) => (
                <div
                  key={label}
                  className={`flex justify-between px-6 py-4 ${i < specRows.length - 1 ? 'border-b border-stone-100' : ''}`}
                >
                  <span className='text-stone-500 text-sm'>{label}</span>
                  <span className='darkblue font-semibold text-sm text-right'>{value}</span>
                </div>
              ))}
            </div>
          )}

          <div>
            <p className='purple font-semibold text-2xl mb-4'>Project Details</p>
            <p className='text-stone-700 leading-relaxed text-justify whitespace-pre-line'>
              {project.projectdetails}
            </p>
          </div>

          {(project.markets?.length > 0 || project.services?.length > 0) && (
            <div className='flex flex-wrap gap-6'>
              {project.markets?.length > 0 && (
                <div>
                  <p className='text-xs text-stone-400 uppercase tracking-wide mb-2'>Markets</p>
                  <div className='flex flex-wrap gap-2'>
                    {project.markets.map((m) => (
                      <span key={m} className='text-xs darkblue bg-[#f2f0fa] border border-[#e3ddf5] rounded-full px-3 py-1'>{m}</span>
                    ))}
                  </div>
                </div>
              )}
              {project.services?.length > 0 && (
                <div>
                  <p className='text-xs text-stone-400 uppercase tracking-wide mb-2'>Services</p>
                  <div className='flex flex-wrap gap-2'>
                    {project.services.map((s) => (
                      <span key={s} className='text-xs darkblue bg-[#f2f0fa] border border-[#e3ddf5] rounded-full px-3 py-1'>{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {gallery.length > 0 && (
          <div className='lg:w-[45%]'>
            <div className='relative rounded-2xl overflow-hidden shadow-lg bg-stone-100'>
              <img
                src={gallery[activeSlide].src}
                alt={gallery[activeSlide].alt || project.Association}
                className='w-full h-[280px] md:h-[320px] object-cover'
              />
              {gallery[activeSlide].caption && (
                <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3'>
                  <p className='text-white text-sm font-medium'>{gallery[activeSlide].caption}</p>
                </div>
              )}
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    aria-label='Previous image'
                    className='absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center shadow'
                  >
                    ‹
                  </button>
                  <button
                    onClick={nextSlide}
                    aria-label='Next image'
                    className='absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center shadow'
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            {gallery.length > 1 && (
              <div className='flex gap-2 mt-3 justify-center'>
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveSlide(i)}
                    aria-label={`Go to image ${i + 1}`}
                    className='w-2 h-2 rounded-full transition-colors'
                    style={{ background: i === activeSlide ? '#8364c3' : '#d6d3d1' }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {project.features?.length > 0 && (
        <div className='bg-[#f7f6fb] py-12'>
          <div className='max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center'>
            {project.features.map((f) => (
              <div key={f.label} className='flex flex-col items-center gap-2'>
                <div className='w-12 h-12 rounded-full bg-white shadow flex items-center justify-center text-xl'>
                  {f.icon}
                </div>
                <p className='darkblue text-sm font-medium'>{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default AllProjectDetails